// src/components/OrdersItemsFromNotifications.tsx
import React, { useMemo } from 'react';
import OrdersItemsTable from './OrdersItemsTable';
import { useNotifications } from '../hooks/useNotifications';

type OrderItem = {
  sku?: string;
  quantity?: number;
  price?: number | null;
  total?: number | null;
};

export type OrdersTableOrder = {
  id?: string;
  phone?: string;
  orderNumber: string;
  createdAt: string;
  status?: string;
  itemType?: string;
  categories?: string[];    // ['spark_plugs', 'ignition_coils', …]
  productTypes?: string[];  // ['original'] | ['copy'] | ['original', 'copy']
  skus?: string[];          // part numbers per ordered product
  items: OrderItem[];
};

type Props = {
  /** Если передаём внешний список уведомлений — адаптер его использует */
  source?: any[];
  maxRows?: number;
  /** Сколько грузить из хука, если source не передан */
  pageSizeHint?: number;
};

const OrdersItemsFromNotifications: React.FC<Props> = ({
  source,
  maxRows = 500,
  pageSizeHint = 10000,
}) => {
  const hook = useNotifications(pageSizeHint);
  const notifItems = source ?? hook.items;

  const orders: OrdersTableOrder[] = useMemo(() => {
    const mapped = (notifItems ?? []).map((n: any) => {
      const id = n?.id ?? n?.number;
      const orderNumber = n?.number ?? String(id ?? '');
      const createdAt = n?.createdAt ?? new Date().toISOString();
      const orderTotal = typeof n?.total === 'number' ? n.total : 0;

      // Используем реальные items из notification (product_price, count, sum)
      const rawItems: any[] = Array.isArray(n?.items) && n.items.length > 0 ? n.items : [];
      const items = rawItems.length > 0
        ? rawItems.map((it: any) => ({
            sku: it.name ?? it.sku ?? '—',
            quantity: Number(it.qty ?? it.quantity ?? 1),
            price: Number(it.price ?? 0) || null,
            total: Number(it.total ?? 0) || null,
          }))
        : [{ sku: '—', quantity: 1, price: orderTotal || null, total: orderTotal || null }];

      return {
        id,
        phone: n?.phone,
        orderNumber,
        createdAt,
        status: n?.status ?? '—',
        itemType: n?.itemType ?? n?.type ?? '—',
        categories: n?.categories,
        productTypes: n?.productTypes,
        skus: n?.skus,
        // Передаём order-level поля напрямую
        orderTotal,
        discount: Number(n?.discount ?? 0),
        items,
      };
    });

    // по дате — новые сверху
    mapped.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return mapped.slice(0, maxRows);
  }, [notifItems, maxRows]);

  const handleStatusChanged = async () => {
    await hook.reload();
  };

  return (
    <OrdersItemsTable
      orders={orders}
      maxRows={maxRows}
      onMarkSold={hook.markSold}
      onStatusChanged={handleStatusChanged}
    />
  );
};

export default OrdersItemsFromNotifications;
